import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

const getStyles = (theme) => ({
  cardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  header: {
    padding: theme.spacing.md,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: theme.colors.text.primary,
    margin: 0,
    lineHeight: '1.3',
  },
  subtitle: {
    fontSize: '14px',
    color: theme.colors.text.secondary,
    margin: 0,
    lineHeight: '1.4',
  },
  content: {
    color: theme.colors.text.primary,
    fontSize: '14px',
    lineHeight: '1.5',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    paddingTop: 0,
    borderTop: `1px solid ${theme.colors.border}`,
    marginTop: theme.spacing.md,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: 'transparent',
    color: theme.colors.text.primary,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    outline: 'none',
  },
  primaryAction: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
    color: theme.colors.text.inverse,
  },
  actionIcon: {
    marginRight: theme.spacing.xs,
    fontSize: '14px',
  },
  emptyState: {
    padding: theme.spacing.xl,
    textAlign: 'center',
    color: theme.colors.text.muted,
    fontStyle: 'italic',
  },
});

/**
 * Structured Card Component
 * Renders content in a card layout with optional header, body, and footer
 */
const StructuredCard = ({
  title,
  subtitle,
  content,
  image,
  actions = [],
  variant = 'default', // 'default', 'outlined', 'elevated'
  clickable = false,
  onClick,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const getVariantStyles = () => {
    const baseStyles = {
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      transition: 'all 0.2s',
    };

    switch (variant) {
      case 'outlined':
        return {
          ...baseStyles,
          border: `1px solid ${theme.colors.border}`,
        };

      case 'elevated':
        return {
          ...baseStyles,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        };

      default:
        return {
          ...baseStyles,
          border: `1px solid ${theme.colors.border}`,
        };
    }
  };

  const handleClick = (e) => {
    if (clickable && onClick) {
      onClick(e);
    }
  };

  const renderImage = () => {
    if (!image) return null;

    const imageStyles = {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      backgroundColor: theme.colors.background.main,
    };

    if (typeof image === 'string') {
      return <img src={image} alt={title || 'Card image'} style={imageStyles} />;
    }

    if (image.src) {
      return (
        <img
          src={image.src}
          alt={image.alt || title || 'Card image'}
          style={{
            ...imageStyles,
            height: image.height || '200px',
            objectFit: image.objectFit || 'cover',
          }}
        />
      );
    }

    return null;
  };

  const renderActions = () => {
    if (!actions || actions.length === 0) return null;

    return (
      <div style={styles.actions}>
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              if (action.onClick) action.onClick();
            }}
            style={{
              ...styles.actionButton,
              ...(action.primary && styles.primaryAction),
            }}
            disabled={action.disabled}
          >
            {action.icon && <span style={styles.actionIcon}>{action.icon}</span>}
            {action.text}
          </button>
        ))}
      </div>
    );
  };

  const cardStyle = {
    ...getVariantStyles(),
    cursor: clickable ? 'pointer' : 'default',
    ...style,
  };

  // If no content is provided, show empty state
  if (!title && !subtitle && !content && !image && (!actions || actions.length === 0)) {
    return (
      <div style={cardStyle} className={className}>
        <div style={styles.emptyState}>No content to display</div>
      </div>
    );
  }

  return (
    <div
      style={cardStyle}
      className={className}
      onClick={handleClick}
      onMouseEnter={(e) => {
        if (clickable) {
          Object.assign(e.currentTarget.style, styles.cardHover);
        }
      }}
      onMouseLeave={(e) => {
        if (clickable) {
          Object.assign(e.currentTarget.style, cardStyle);
        }
      }}
    >
      {renderImage()}

      {(title || subtitle) && (
        <div
          style={{
            ...styles.header,
            paddingBottom: content ? theme.spacing.sm : theme.spacing.md,
          }}
        >
          {title && (
            <h3
              style={{
                ...styles.title,
                marginBottom: subtitle ? theme.spacing.xs : 0,
              }}
            >
              {title}
            </h3>
          )}
          {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
        </div>
      )}

      {content && (
        <div
          style={{
            ...styles.content,
            padding: `0 ${theme.spacing.md} ${theme.spacing.md}`,
          }}
        >
          {typeof content === 'string' ? <p style={{ margin: 0 }}>{content}</p> : content}
        </div>
      )}

      {renderActions()}
    </div>
  );
};

export default StructuredCard;
